import { createContext, useReducer, useCallback } from "react";
import axios from "../../utils/axios/v1/userAxios";

const GET_DDOS_CONFIG = "GET_DDOS_CONFIG";
const GET_AD_CONFIG = "GET_AD_CONFIG";
const GET_DDOS_OBSERVABILITY = "GET_DDOS_OBSERVABILITY";
const GET_DDOS_TREND = "GET_DDOS_TREND";
const GET_AD_ENGINE_HEALTH = "GET_AD_ENGINE_HEALTH";
const GET_ROUTE_CONFIGS = "GET_ROUTE_CONFIGS";
const GET_STAGE = "GET_STAGE";
const SET_ERROR = "SET_ERROR";

const initialState = {
  ddosConfig: null,
  adConfig: null,
  ddosObservability: null,
  ddosTrend: null,
  adEngineHealth: null,
  // Sentinel-X: per-route detection thresholds (ddos_route_configs).
  // null = not loaded, [] = loaded + empty.
  routeConfigs: null,
  // Sentinel-X: enrollment stage override. null means no explicit row —
  // the brain falls back to sensitivity-derived stage.
  detectorStage: null,
  site_id: "all",
  errMsg: null,
};

const DdosConfigReducer = (state, action) => {
  switch (action.type) {
    case GET_DDOS_CONFIG:
      return {
        ...state,
        ddosConfig: action.payload.ddosConfig,
      };
    case GET_AD_CONFIG:
      return {
        ...state,
        adConfig: action.payload.adConfig,
      };
    case GET_DDOS_OBSERVABILITY:
      return {
        ...state,
        ddosObservability: action.payload.ddosObservability,
      };
    case GET_DDOS_TREND:
      return {
        ...state,
        ddosTrend: action.payload.ddosTrend,
      };
    case GET_AD_ENGINE_HEALTH:
      return {
        ...state,
        adEngineHealth: action.payload.adEngineHealth,
      };
    case GET_ROUTE_CONFIGS:
      return {
        ...state,
        routeConfigs: action.payload.routeConfigs,
      };
    case GET_STAGE:
      return {
        ...state,
        detectorStage: action.payload.detectorStage,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const DdosConfigContext = createContext(null);

function DdosConfigProvider({ children }) {
  const [state, dispatch] = useReducer(DdosConfigReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);

  const getDdosConfig = useCallback(
    async (siteUid) => {
      dispatch({
        type: GET_DDOS_CONFIG,
        payload: {
          ddosConfig: null,
        },
      });
      try {
        const response = await axios.get(`config/ddos/${siteUid}/config`);
        dispatch({
          type: GET_DDOS_CONFIG,
          payload: {
            ddosConfig: response.data,
          },
        });
      } catch (err) {
        dispatch({
          type: GET_DDOS_CONFIG,
          payload: {
            ddosConfig: null,
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const checkBrowserIntegrity = useCallback(
    async (siteUid, browser_integrity) => {
      try {
        const response = await axios.patch(`config/ddos/${siteUid}/config`, {
          browser_integrity,
        });
        dispatch({
          type: GET_DDOS_CONFIG,
          payload: {
            ddosConfig: response.data,
          },
        });
      } catch (err) {
        setErr(err.message);
        await getDdosConfig(siteUid);
      }
    },
    [setErr, getDdosConfig]
  );

  const setDdosSensitivity = useCallback(
    async (siteUid, sensitivity) => {
      try {
        const response = await axios.patch(`config/ddos/${siteUid}/config`, { sensitivity });
        dispatch({
          type: GET_DDOS_CONFIG,
          payload: {
            ddosConfig: response.data,
          },
        });
      } catch (err) {
        setErr(err.message);
        await getDdosConfig(siteUid);
      }
    },
    [setErr, getDdosConfig]
  );

  const setDdosMitigationTimeout = useCallback(
    async (siteUid, timeout) => {
      try {
        const response = await axios.patch(`config/ddos/${siteUid}/config`, { timeout });
        dispatch({
          type: GET_DDOS_CONFIG,
          payload: {
            ddosConfig: response.data,
          },
        });
      } catch (err) {
        setErr(err.message);
        await getDdosConfig(siteUid);
      }
    },
    [setErr, getDdosConfig]
  );

  const getAdConfig = useCallback(
    async (siteUid) => {
      dispatch({
        type: GET_AD_CONFIG,
        payload: { adConfig: null },
      });
      try {
        const response = await axios.get(`config/ddos/${siteUid}/ad-config`);
        dispatch({
          type: GET_AD_CONFIG,
          payload: { adConfig: response.data },
        });
      } catch (err) {
        dispatch({
          type: GET_AD_CONFIG,
          payload: { adConfig: null },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const updateAdConfig = useCallback(
    async (siteUid, data) => {
      try {
        const response = await axios.put(`config/ddos/${siteUid}/ad-config`, data);
        dispatch({
          type: GET_AD_CONFIG,
          payload: { adConfig: response.data },
        });
        return true;
      } catch (err) {
        setErr(err.message);
        await getAdConfig(siteUid);
        return false;
      }
    },
    [setErr, getAdConfig]
  );

  const clearAdConfigOverride = useCallback(
    async (siteUid, key) => {
      try {
        await axios.delete(`config/ddos/${siteUid}/ad-config/${key}`);
        await getAdConfig(siteUid);
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr, getAdConfig]
  );

  const getDdosObservability = useCallback(
    async (siteUid) => {
      try {
        const response = await axios.get(`config/ddos/${siteUid}/observability`);
        dispatch({
          type: GET_DDOS_OBSERVABILITY,
          payload: { ddosObservability: response.data },
        });
      } catch (err) {
        dispatch({
          type: GET_DDOS_OBSERVABILITY,
          payload: { ddosObservability: null },
        });
        // Non-fatal: the strip degrades to "—" when observability is unavailable.
      }
    },
    []
  );

  const getDdosTrend = useCallback(
    async (siteUid) => {
      try {
        const response = await axios.get(`config/ddos/${siteUid}/trend`);
        dispatch({
          type: GET_DDOS_TREND,
          payload: { ddosTrend: response.data },
        });
      } catch (err) {
        dispatch({
          type: GET_DDOS_TREND,
          payload: { ddosTrend: null },
        });
      }
    },
    []
  );

  const getAdEngineHealth = useCallback(async () => {
    try {
      const response = await axios.get(`config/ddos/ad-engine-health`);
      dispatch({
        type: GET_AD_ENGINE_HEALTH,
        payload: { adEngineHealth: response.data },
      });
    } catch (err) {
      dispatch({
        type: GET_AD_ENGINE_HEALTH,
        payload: { adEngineHealth: null },
      });
    }
  }, []);

  // Toggle the master kill-switch. UI calls this from the Protection card.
  const setDdosEnabled = useCallback(
    async (siteUid, enabled) => {
      try {
        const response = await axios.patch(`config/ddos/${siteUid}/config`, { enabled });
        dispatch({
          type: GET_DDOS_CONFIG,
          payload: { ddosConfig: response.data },
        });
        return true;
      } catch (err) {
        setErr(err.message);
        await getDdosConfig(siteUid);
        return false;
      }
    },
    [setErr, getDdosConfig]
  );

  // ===== Sentinel-X — per-route configs =====
  // Mounted on /api/user/v1/config/ddos/:site_uid/routes. 60s brain
  // refresh means a CRUD change propagates to the data plane within
  // one tick; the UI reload after each mutation shows the fresh
  // state immediately.

  const getRouteConfigs = useCallback(
    async (siteUid) => {
      try {
        const response = await axios.get(`config/ddos/${siteUid}/routes`);
        dispatch({
          type: GET_ROUTE_CONFIGS,
          payload: { routeConfigs: response.data },
        });
      } catch (err) {
        dispatch({
          type: GET_ROUTE_CONFIGS,
          payload: { routeConfigs: [] },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const createRouteConfig = useCallback(
    async (siteUid, data) => {
      try {
        await axios.post(`config/ddos/${siteUid}/routes`, data);
        await getRouteConfigs(siteUid);
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr, getRouteConfigs]
  );

  const updateRouteConfig = useCallback(
    async (siteUid, routeId, data) => {
      try {
        await axios.patch(`config/ddos/${siteUid}/routes/${routeId}`, data);
        await getRouteConfigs(siteUid);
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr, getRouteConfigs]
  );

  const deleteRouteConfig = useCallback(
    async (siteUid, routeId) => {
      try {
        await axios.delete(`config/ddos/${siteUid}/routes/${routeId}`);
        await getRouteConfigs(siteUid);
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr, getRouteConfigs]
  );

  // ===== Sentinel-X — enrollment stage =====
  // null stage = the brain's sensitivity-derived default is in
  // effect. An explicit row (shadow|canary|authoritative|off)
  // overrides. `off` pauses the site without flipping the kill-
  // switch — useful during incident triage.

  const getDetectorStage = useCallback(
    async (siteUid) => {
      try {
        const response = await axios.get(`config/ddos/${siteUid}/stage`);
        dispatch({
          type: GET_STAGE,
          payload: { detectorStage: response.data },
        });
      } catch (err) {
        dispatch({
          type: GET_STAGE,
          payload: { detectorStage: null },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const setDetectorStage = useCallback(
    async (siteUid, stage, canaryPct) => {
      try {
        const body = { stage };
        if (canaryPct !== undefined) body.canaryPct = canaryPct;
        await axios.put(`config/ddos/${siteUid}/stage`, body);
        await getDetectorStage(siteUid);
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr, getDetectorStage]
  );

  const clearDetectorStage = useCallback(
    async (siteUid) => {
      try {
        await axios.delete(`config/ddos/${siteUid}/stage`);
        await getDetectorStage(siteUid);
        return true;
      } catch (err) {
        setErr(err.message);
        return false;
      }
    },
    [setErr, getDetectorStage]
  );

  return (
    <DdosConfigContext.Provider
      value={{
        ...state,
        getDdosConfig,
        checkBrowserIntegrity,
        setDdosSensitivity,
        setDdosMitigationTimeout,
        setDdosEnabled,
        getAdConfig,
        updateAdConfig,
        clearAdConfigOverride,
        getDdosObservability,
        getDdosTrend,
        getAdEngineHealth,
        // Sentinel-X per-route + stage
        getRouteConfigs,
        createRouteConfig,
        updateRouteConfig,
        deleteRouteConfig,
        getDetectorStage,
        setDetectorStage,
        clearDetectorStage,
        setErr,
      }}
    >
      {children}
    </DdosConfigContext.Provider>
  );
}

export { DdosConfigContext, DdosConfigProvider };
